import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Flex, ProgressCircle } from "./Common/spectrumCompat";
import DxpRegistrationTopHeader from "./DxpRegistrationTopHeader";
import "./css/registration.css";
import EnterEmail from "./EnterEmail";
import SelectYourCompany from "./SelectYourCompany";
import AdobeIdVerification from "./AdobeIdVerification";
import UserProfile from "./UserProfile";
import CompanyInformation from "./CompanyInformation";
import MembershipOptions from "./MembershipOptions";
import { setPartnerRegistrationFormStep } from "../Redux/actions";
import { ensureDxpAdobeImsBootstrapped } from "../Helpers/dxpImsBootstrap";
import useDxpEmailMismatchGuard from "../Hooks/useDxpEmailMismatchGuard";
import {
  clearDxpPartnerResumePending,
  DXP_PENDING_PARTNER_RESUME_ID_KEY,
  hydrateDxpBenefitsCenterReturnIfNeeded,
  hydrateDxpRegistrationResumeIfNeeded,
  isDxpInvitedContactRegistration,
  prepareDxpInvitedContactWizardState,
  shouldHydrateDxpBenefitsCenterReturn,
  syncResumeHydrationToRedux,
} from "../Helpers/hydratePartnerRegistrationSession";

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-enable @typescript-eslint/no-explicit-any */

const PROGRAM = "DXP";

type AppState = {
  registration?: {
    partnerRegistrationFormStep?: number;
    registeredCompanies?: unknown[];
    selectedRegisterCompanyType?: string | null;
  };
};

/**
 * Wrapper that shows:
 * - EnterEmail (Step 0) when: partnerRegistrationFormStep === 0 && registeredCompanies.length === 0
 * - SelectYourCompany (Step 1, or step 0 with companies from session) when applicable
 * - AdobeIdVerification (Step 2) when: partnerRegistrationFormStep === 2
 * - UserProfile (Step 3) for all flows
 * - CompanyInformation / MembershipOptions for steps 4–5 when company type matches
 *
 * Unknown step values reset to step 0 (Enter email / resume flow).
 * Full browser refresh while on Company information (step 4) resets the saved step to 3
 * (User profile) in Redux/localStorage (see registration reducer initial state).
 */
function RegistrationWizardWrapper() {
  const dispatch = useDispatch();
  const registration = useSelector(
    (state: AppState) => state?.registration || {},
  );

  const [isAdobeIMSLoaded, setIsAdobeIMSLoaded] = useState(
    () => typeof window !== "undefined" && !!window.adobeIMS,
  );
  const [resumeHydrating, setResumeHydrating] = useState(
    () =>
      typeof window !== "undefined" &&
      !!localStorage.getItem(DXP_PENDING_PARTNER_RESUME_ID_KEY) &&
      !isDxpInvitedContactRegistration(),
  );

  const partnerRegistrationFormStep =
    registration?.partnerRegistrationFormStep ?? 0;
  const registeredCompanies = registration?.registeredCompanies ?? [];

  useEffect(() => {
    Promise.resolve(ensureDxpAdobeImsBootstrapped()).catch(() => undefined);
  }, []);

  useEffect(() => {
    const checkAdobeIMS = window.setInterval(() => {
      if (window.adobeIMS) {
        setIsAdobeIMSLoaded(true);
        window.clearInterval(checkAdobeIMS);
      }
    }, 100);
    return () => window.clearInterval(checkAdobeIMS);
  }, []);

  useEffect(() => {
    if (!isAdobeIMSLoaded || !window.adobeIMS?.isSignedInUser?.()) {
      return () => undefined;
    }
    if (isDxpInvitedContactRegistration()) {
      return () => undefined;
    }

    if (!shouldHydrateDxpBenefitsCenterReturn()) {
      return () => undefined;
    }

    let cancelled = false;
    setResumeHydrating(true);
    hydrateDxpBenefitsCenterReturnIfNeeded()
      .then((ok) => {
        if (cancelled || !ok) return;
        clearDxpPartnerResumePending();
        syncResumeHydrationToRedux(dispatch);
        dispatch(setPartnerRegistrationFormStep(5));
      })
      .catch(() => undefined)
      .finally(() => {
        setResumeHydrating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAdobeIMSLoaded]);

  useEffect(() => {
    if (!isAdobeIMSLoaded) {
      return () => undefined;
    }

    let cancelled = false;

    const tryAdvanceAfterIms = async (): Promise<boolean> => {
      if (localStorage.getItem("origin") !== "IMSPage") return false;
      if (!window.adobeIMS?.isSignedInUser?.()) return false;

      const invitedContact = isDxpInvitedContactRegistration();
      const pendingResume =
        !invitedContact &&
        !!localStorage.getItem(DXP_PENDING_PARTNER_RESUME_ID_KEY);
      if (invitedContact) {
        prepareDxpInvitedContactWizardState();
        if (!cancelled) setResumeHydrating(false);
      } else if (pendingResume) {
        setResumeHydrating(true);
        try {
          const ok = await hydrateDxpRegistrationResumeIfNeeded();
          if (cancelled) return true;
          if (!ok) {
            localStorage.setItem("partnerRegistrationFormStep", "3");
          }
        } finally {
          if (!cancelled) setResumeHydrating(false);
        }
      }

      syncResumeHydrationToRedux(dispatch);

      const rawStep = localStorage.getItem("partnerRegistrationFormStep");
      const parsedStep = rawStep ? parseInt(rawStep, 10) : 0;
      const savedStep = Number.isNaN(parsedStep) ? 0 : parsedStep;
      if (invitedContact) {
        dispatch(setPartnerRegistrationFormStep(3));
      } else if (savedStep >= 4) {
        dispatch(setPartnerRegistrationFormStep(savedStep));
      } else {
        dispatch(setPartnerRegistrationFormStep(3));
      }
      localStorage.removeItem("origin");
      return true;
    };

    let poll: number | undefined;
    const run = () => {
      tryAdvanceAfterIms()
        .then((done) => {
          if (done && poll) window.clearInterval(poll);
        })
        .catch(() => undefined);
    };

    if (localStorage.getItem("origin") === "IMSPage") {
      run();
    }

    poll = window.setInterval(run, 200);

    const done = window.setTimeout(() => window.clearInterval(poll), 45000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
      window.clearTimeout(done);
    };
  }, [dispatch, isAdobeIMSLoaded]);

  const showEnterEmail =
    partnerRegistrationFormStep === 0 && registeredCompanies.length === 0;
  const showSelectCompany =
    partnerRegistrationFormStep === 1 ||
    (partnerRegistrationFormStep === 0 && registeredCompanies.length > 0);
  const showAdobeIdVerification = partnerRegistrationFormStep === 2;
  const selectedRegisterCompanyType =
    registration?.selectedRegisterCompanyType ??
    localStorage.getItem("selectedRegisterCompanyType");
  const emailMismatchContinue = localStorage.getItem("emailMismatch");
  const onUserProfileStep =
    partnerRegistrationFormStep === 3 && !emailMismatchContinue;
  const isImsSignedIn =
    isAdobeIMSLoaded && !!window.adobeIMS?.isSignedInUser?.();
  const shouldCheckEmailMismatch = onUserProfileStep && isImsSignedIn;
  const isCheckingEmailMismatch = useDxpEmailMismatchGuard(
    shouldCheckEmailMismatch,
  );
  const awaitingImsForUserProfile = onUserProfileStep && !isImsSignedIn;
  const showUserProfile =
    onUserProfileStep && isImsSignedIn && !isCheckingEmailMismatch;
  const showCompanyInformation =
    partnerRegistrationFormStep === 4 &&
    ["subsidaryCompany", "standaloneCompany"].includes(
      selectedRegisterCompanyType || "",
    );
  const showMembershipOptions =
    partnerRegistrationFormStep === 5 &&
    selectedRegisterCompanyType === "standaloneCompany";

  const matched =
    showEnterEmail ||
    showSelectCompany ||
    onUserProfileStep ||
    showCompanyInformation ||
    showMembershipOptions ||
    showAdobeIdVerification;

  const pendingPartnerResume = !!localStorage.getItem(
    DXP_PENDING_PARTNER_RESUME_ID_KEY,
  );

  useEffect(() => {
    if (resumeHydrating || pendingPartnerResume) return;
    if (!matched) {
      localStorage.removeItem("partnerRegistrationFormStep");
      dispatch(setPartnerRegistrationFormStep(0));
    }
  }, [matched, dispatch, resumeHydrating, pendingPartnerResume]);

  useEffect(() => {
    if (partnerRegistrationFormStep === 4) {
      window.scrollTo(0, 0);
    }
  }, [partnerRegistrationFormStep]);

  if (
    !matched ||
    resumeHydrating ||
    isCheckingEmailMismatch ||
    awaitingImsForUserProfile
  ) {
    return (
      <Flex
        width="100%"
        minHeight="92vh"
        alignItems="center"
        justifyContent="center"
        backgroundColor="gray-100"
      >
        <ProgressCircle aria-label="Loading" isIndeterminate size="L" />
      </Flex>
    );
  }

  if (showEnterEmail) {
    return (
      <div className="registration-page-wrapper dxp-registration-page-shell">
        <DxpRegistrationTopHeader program={PROGRAM} logoId="image" />

        <div className="registration-content-wrapper dxp-registration-content-row">
          <div className="dxp-wizard-route-column">
            <EnterEmail />
          </div>
        </div>
      </div>
    );
  }

  if (showSelectCompany) {
    return <SelectYourCompany />;
  }

  if (showUserProfile) {
    return <UserProfile />;
  }

  if (showCompanyInformation) {
    return <CompanyInformation />;
  }

  if (showMembershipOptions) {
    return <MembershipOptions />;
  }

  if (showAdobeIdVerification) {
    return <AdobeIdVerification />;
  }

  return null;
}

export default RegistrationWizardWrapper;
